/**
 * 손이 앱에 닿는 모든 곳 - 조작 레이어 · 캔버스 제스처 · 키보드, 그리고 그 셋이 공유하는 **모드**.
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 9.4절 · `이상한방-컨셉시트-v0.1.md` 4.4절(원래 방)
 *
 * 셋을 한 파일에 둔 이유는 하나뿐이다. **탭 한 번의 뜻을 정하는 것이 `mode`**인데
 * (게임 모드면 사격, 배치 모드면 위치 지정), 그 값을 읽는 곳이 정확히 이 셋이다 -
 * 화면 위 버튼(`onToggleMode`)·캔버스 탭·`Tab` 키. 갈라 두면 모드가 한쪽에 살고 나머지 둘이
 * 그것을 조회하는 모양이 되는데, 그러면 "모드를 바꾸는 순서"(세션을 먼저 재우고 에디터가
 * 방을 이어받는다)가 소유자 밖에서 불리기 시작한다.
 *
 * 대신 여기에 **규칙은 없다.** 페이즈를 넘기는 것도 원래 방에 들어가는 것도 세션에게 말만 하고,
 * 판단은 전부 리듀서가 한다. 이 파일이 아는 것은 "지금 누른 것이 무엇인가"까지다.
 */

import { CATALOG_BY_ID } from '../catalog/catalog'
import type { ChangerTool } from '../input/changerTool'
import type { DecorateTool } from '../input/decorateTool'

/**
 * 손에 든 도구. 바꾸기와 꾸미기의 **공통 표면**이라 이 층은 어느 쪽인지 모른다 - 두 도구는 배타적인 페이즈에 서고
 * 여기서 하는 일은 같다(겨누기 · 놓기 · "지금 자리를 고르는 중인가"). 구조적으로 맞는지는 컴파일이 검사한다.
 */
type HandTool = Pick<ChangerTool & DecorateTool, 'tapAt' | 'aimAt' | 'isPlacing'>
import { createPointerGestures } from '../input/pointerInput'
import { pickAt } from '../input/tapPicker'
import type { SceneCtx } from '../render/scene'
import { createMobileControls, type AimStatus } from '../ui/mobileControls'
import { isSettingsOpen } from '../ui/settings'
import type { AppMode, AppScreen } from './appMode'
import type { Stage } from './stage'

/**
 * 배치 모드가 없을 때 조작 레이어에 넘길 조준 상태.
 * 확인 바는 관찰 모드에서 접히므로 아무도 읽지 않지만, `render`는 값을 요구한다.
 */
const NO_AIM: AimStatus = {
  action: 'none',
  canCommit: false,
  subjectName: null,
  isRotateLocked: false,
}

/**
 * 핀치·휠 배율을 정규화 줌 델타로 바꾸는 나눗수 = `ln(최대배율 / 최소배율)`.
 *
 * 정규화 줌(0~1)은 `camera.zoom`에 **기하급수**로 매핑되므로(`isoCamera.ts`),
 * 배율 r의 변화는 `ln(r) / ln(전체범위)`만큼의 정규화 이동이다.
 *
 * 이 값이 근사치인 것은 의도다. 실제 전체 범위는 종횡비에 따라 약 10~33배로 달라지는데
 * (세로로 긴 폰일수록 넓다), 정확한 값을 얻으려면 카메라를 양 끝으로 한 번씩 밀어 봐야 하고
 * 그 과정에서 줌아웃 클램프가 **시점을 방 중심으로 되돌려 버린다** - 핀치할 때마다 화면이
 * 제자리로 튄다는 뜻이다. 여기서 필요한 것은 배율의 절댓값이 아니라 **손가락 대비 변화 속도**이고,
 * 양 끝(전체 조망 / 소품 클로즈업)은 카메라가 이미 보장한다. ln(20) ≈ 3.0을 중앙값으로 잡았다.
 */
const ZOOM_LOG_SPAN = 3.0

/* --- 키 --------------------------------------------------------------------- */

/** 게임 ↔ 배치 전환. 이 키 하나가 두 모드를 가른다. */
const MODE_TOGGLE_KEY = 'Tab'

/** 현재 페이즈를 즉시 끝낸다. 혼자 수십 판을 돌리는 이번 단계의 핵심 도구다. */
const SKIP_PHASE_KEY = 'Space'

/**
 * 정답 방 워프(컨셉시트 4.4). Q/E는 시점 회전, R/C/X는 배치 동사가 이미 쓰고 있어 F를 골랐다.
 * 쿨타임·체류 판단은 전부 리듀서가 한다 - 여기서는 토글 신호만 보낸다.
 */
const ORIGIN_WARP_KEY = 'KeyF'

/*
 * **재장전에는 단축키를 주지 않았다.** `R`을 붙여 놓고 한 번 물러섰으므로 근거를 남긴다.
 *
 * 탄창 칩은 거부됐을 때 **왜 못 쓰는지를 스스로 말한다**(잔탄 가득 / 이번 판에 다 씀).
 * 그 판단은 HUD가 마지막 뷰를 읽어서 하고, 이유를 고르는 규칙은 거기 한 곳에만 있다.
 * 키를 붙이면 같은 행동에 **설명을 못 싣는 두 번째 입구**가 생겨, 눌렀는데 아무 일도
 * 일어나지 않는 순간이 만들어진다 - 세션이 정답 방 거부(`originEntryDenied`)에서
 * 굳이 토스트를 띄우는 이유와 정확히 같은 이유로 그건 고장으로 읽힌다.
 *
 * 칩은 수색 내내 화면에 떠 있고 얼굴만으로도 쓸 수 있는지를 말하므로, 단축키가 버는 것은
 * 커서 이동 한 번뿐이다. 나중에 정말 필요하면 붙일 곳은 여기가 아니라 HUD다 -
 * "칩을 눌러라"를 HUD가 내주면 두 입구가 같은 설명을 공유한다.
 */

export type InputLayer = Readonly<{
  /** 지금 손이 무엇을 하는가. 프레임 루프는 이 값을 묻지 않지만 개발 훅과 키 처리기는 묻는다. */
  getMode: () => AppMode
  setMode: (next: AppMode) => void
  /**
   * 무대가 갈렸다. **`setMode`를 부르지 않는다** - 그쪽은 이미 없어진 무대의 에디터와 세션을
   * 만지므로, 여기서 필요한 것은 값을 되돌리는 것뿐이다.
   */
  resetToGameMode: () => void
  /** 캔버스 탭 한 번. 제스처와 개발 훅이 **같은 함수**를 쓴다(아래 주석). */
  tapAt: (clientX: number, clientY: number) => void
  /** 한 프레임의 화면 갱신. 프레임 루프와 무대 교체가 부른다. */
  refresh: () => void
  dispose: () => void
}>

export type InputLayerDeps = Readonly<{
  ctx: SceneCtx
  canvas: HTMLCanvasElement
  /** `?dev=1`. 배치 모드가 아예 없으면 전환 버튼도 `Tab` 가로채기도 없다. */
  devTools: boolean
  getStage: () => Stage | null
  getScreen: () => AppScreen
}>

export function createInputLayer(deps: InputLayerDeps): InputLayer {
  const { ctx, canvas, devTools, getStage, getScreen } = deps

  let mode: AppMode = 'game'

  /* --- 조작 레이어 ---------------------------------------------------------- */

  const controls = createMobileControls({
    mount: document.body,
    handlers: {
      onRotateView: (delta) => getStage()?.iso.rotate(delta),
      onZoomStep: (delta) => {
        const iso = getStage()?.iso
        iso?.setZoom(iso.getZoom() + delta)
      },
      onZoomTo: (zoom) => getStage()?.iso.setZoom(zoom),
      onToggleMode: () => setMode(mode === 'game' ? 'build' : 'game'),
      onConfirm: () => {
        getStage()?.editor?.commitAim()
      },
      onCancel: () => getStage()?.editor?.clearAim(),
      onRotateSubject: () => getStage()?.editor?.rotateSubject(),
      onRemove: () => getStage()?.editor?.removeSelected(),
    },
  })

  /*
   * 모드 전환 버튼을 걷어 낸다. `?dev=1`이 없으면 갈 곳이 없는 버튼이고, 그보다 나쁘게는
   * **수색 중에 정답을 여는 문**으로 보인다(9.4절).
   *
   * `createMobileControls`에 "이 버튼은 빼라"는 옵션이 아직 없어서 만들어진 노드를 지운다.
   * 감추지 않고 지우는 이유는 두 가지다 - `display: flex`가 `[hidden]`을 이겨서 감추려면
   * 남의 파일의 선택자를 알아야 하고, 남겨 두면 화면 밖에서 탭 순서에는 계속 잡힌다.
   * 조작 레이어는 이 노드가 문서에 붙어 있는지 묻지 않으므로(라벨만 갱신한다) 지워도 안전하다.
   */
  if (!devTools) {
    document.querySelector('.mc-root .mc-mode')?.remove()
  }

  /**
   * 배치 모드 화면 갱신. **매 프레임 통째로 다시 그린다.**
   *
   * 게임 HUD는 `session`이 스스로 그리므로 여기서는 배치 레이어만 본다.
   * 게임 모드에서 이 함수가 하는 일은 조작 레이어(줌 슬라이더·나침반) 갱신뿐이다.
   */
  function refresh(): void {
    const stage = getStage()
    if (stage === null) return
    const editor = stage.editor
    const isBuilding = mode === 'build' && editor !== null

    if (isBuilding) {
      // 두 세션(싱글·여럿이)이 같은 모양으로 답한다(`stageSession.ts`). 여기서 어느 쪽인지 묻지 않는다.
      const info = stage.session.getDebugInfo()
      editor.hud.renderGame({
        mode: 'place',
        ammo: info.ammo,
        ammoMax: stage.tuning.startAmmo,
        seed: info.seed,
        objectCount: editor.getRoom().objects.size,
        lastShot: null,
      })
    }

    controls.render({
      mode: isBuilding ? 'place' : 'observe',
      zoom: stage.iso.getZoom(),
      direction: stage.iso.getDirection(),
      aim: editor?.getAim() ?? NO_AIM,
    })
  }

  /**
   * 모드 전환. **카메라는 건드리지 않는다** - 보던 곳을 계속 보면서 손이 하는 일만 바뀐다.
   *
   * 순서가 규약이다. 배치로 들어갈 때는 세션을 먼저 재우고(씬 소유권을 놓게 하고) 에디터가
   * 화면의 방을 이어받는다. 나올 때는 반대로, 에디터를 끈 다음 세션이 씬을 자기 방으로 되돌린다.
   * 순서를 뒤집으면 한 프레임 동안 두 소유자가 같은 씬을 서로 다른 방으로 덮어쓴다.
   */
  function setMode(next: AppMode): void {
    const stage = getStage()
    // 선택·안내 화면 위에서 배치 모드로 들어가면 오버레이 뒤에서 방이 편집된다.
    if (stage === null || getScreen() !== 'play' || mode === next) return
    // 에디터가 없으면 모드도 하나뿐이다. `mode`를 옮겨 놓기만 하면 조작 레이어가
    // 확인 바를 펴 놓고 아무 데도 닿지 않는 화면이 된다.
    const editor = stage.editor
    if (editor === null) return
    mode = next

    if (next === 'build') {
      stage.session.setActive(false)
      editor.setRoom(stage.session.getVisibleRoom())
      editor.setActive(true)
      editor.hud.setVisible(true)
    } else {
      editor.setActive(false)
      editor.hud.setVisible(false)
      stage.session.setActive(true)
    }

    refresh()
  }

  /* --- 탭 --------------------------------------------------------------------- */

  /**
   * 배치 모드의 탭 = **위치 지정**(확정이 아니다).
   *
   * 손가락에는 hover가 없어서 "놓기 전에 보기"가 성립하지 않는다. 그래서 지정과 확정을 갈랐고,
   * 그 사이에 줌·회전·팬으로 확인하고 소품을 돌리거나 색을 바꿀 수 있다.
   * 같은 칸을 다시 탭하면 확정된다(에디터가 확인 버튼과 같은 경로로 처리한다).
   */
  function aimAt(clientX: number, clientY: number): void {
    const stage = getStage()
    if (stage === null) return
    const editor = stage.editor
    if (editor === null) return

    const result = pickAt(
      ctx,
      clientX,
      clientY,
      editor.getRoom(),
      CATALOG_BY_ID,
      stage.bounds,
      { mode: 'place' },
    )
    // `place` 모드는 배치 결과만 돌려준다. 다른 종류가 오면 그건 판정 계층의 계약 위반이므로
    // 조용히 처리하지 않고 아무 일도 하지 않는다(허공 탭과 같은 취급).
    if (result === null || result.kind !== 'place') return

    if (editor.aimAt(result.cell, result.overInstanceId) === 'committed') {
      controls.flashTap(clientX, clientY, 'object')
    }
    refresh()
  }

  /**
   * 탭 한 번이 뜻하는 것. 손가락과 개발 훅이 **같은 함수**를 통과한다.
   *
   * 한때 이 몸통은 두 벌이었다(제스처 쪽 하나, `__weirdRoom.tapAt` 쪽 하나). 자동 검증이
   * 사람의 탭과 같은 길을 가지 않으면 그 검증은 자기 자신을 검증하는 것이라, 화면 가드
   * (`screen !== 'play'`)를 한쪽에서만 고치는 날 조용히 갈린다.
   */
  function tapAt(clientX: number, clientY: number): void {
    const stage = getStage()
    // 오버레이가 떠 있는 동안 캔버스 탭은 아무 일도 하지 않는다. 뒤에 있는 방은 배경이다.
    if (getScreen() !== 'play' || stage === null) return
    if (mode !== 'game') {
      aimAt(clientX, clientY)
      return
    }
    /*
     * 손에 도구가 있으면 탭은 사격이 아니라 **고르기·자리 정하기**다(M4 바꾸기 · M10 꾸미기). 그 판단을 여기서 페이즈와
     * 역할로 다시 하지 않는다 - 도구는 그때만 서 있고(`stageChanger.ts`·`stageDecorate.ts`) 그 밖에는 `null`이므로
     * "도구가 있는가"가 곧 답이다. 둘이 함께 설 일은 없다(배타적인 페이즈다).
     */
    const hand = stage.changer() ?? stage.decorator()
    if (hand !== null) hand.tapAt(clientX, clientY)
    else stage.session.shootAt(clientX, clientY)
  }

  /* --- 입력 배선 --------------------------------------------------------------- */

  const gestures = createPointerGestures(canvas)

  gestures.onTap(tapAt)

  /**
   * 자리를 고르는 중인가. 참이면 드래그는 화면을 옮기는 대신 **겨눈다** - "놓을 곳을 정확히 보고 놓는다"가
   * 이 모드의 목적이고, 같은 손가락이 두 뜻을 가질 수는 없다. 판단은 도구 하나가 한다(`isPlacing`) -
   * 여기서 페이즈나 모드를 다시 보면 그 판단의 두 번째 사본이다.
   */
  function placingTool(): HandTool | null {
    if (getScreen() !== 'play') return null
    const stage = getStage()
    const hand = stage?.changer() ?? stage?.decorator() ?? null
    return hand !== null && hand.isPlacing() ? hand : null
  }

  gestures.onAimMove((x, y) => placingTool()?.aimAt(x, y))

  // 끌어서 겨누고 뗀 자리에 놓는다. 짧은 탭은 `onTap`이 받으므로 둘은 배타적이다(`pointerInput.onAimEnd`).
  gestures.onAimEnd((x, y) => placingTool()?.tapAt(x, y))

  gestures.onPanDelta((dx, dy) => {
    if (placingTool() !== null) return
    getStage()?.iso.panBy(dx, dy)
  })

  gestures.onPinchZoom((scaleDelta, centerX, centerY) => {
    const stage = getStage()
    if (stage === null) return
    if (!Number.isFinite(scaleDelta) || scaleDelta <= 0) return

    // 앵커는 **캔버스 좌상단 기준** CSS 픽셀이다(isoCamera 계약). 클라이언트 좌표를 그대로 넘기면
    // 캔버스가 화면을 꽉 채우지 않는 배치에서 줌 중심이 어긋난다.
    const rect = canvas.getBoundingClientRect()
    stage.iso.setZoom(
      stage.iso.getZoom() + Math.log(scaleDelta) / ZOOM_LOG_SPAN,
      centerX - rect.left,
      centerY - rect.top,
    )
  })

  gestures.onRotateRequest((delta) => getStage()?.iso.rotate(delta))

  function handleKeyDown(ev: KeyboardEvent): void {
    /*
     * 설정 패널이 떠 있으면 키는 전부 그쪽의 것이다. 비켜서지 않으면 패널 안에서 스페이스로
     * 스위치를 누르는 순간 **뒤에서 페이즈가 넘어간다** - 설정을 열었다는 이유로 외우기 60초를
     * 잃는 것이다. 포인터는 스크림이 막지만 키는 창까지 올라오므로 여기서 한 번 더 막는다.
     */
    if (isSettingsOpen()) return
    // 선택·안내 화면은 자기 단축키(숫자키 · Enter · Esc)를 스스로 처리한다.
    const stage = getStage()
    if (getScreen() !== 'play' || stage === null) return

    // Tab을 가로채는 것은 배치 모드가 있을 때뿐이다. 없을 때까지 막으면 키보드 탐색이
    // 캔버스에 갇혀, 언어 토글과 HUD 버튼에 손이 닿지 않는다.
    if (ev.code === MODE_TOGGLE_KEY && devTools) {
      // 막지 않으면 브라우저가 포커스를 다음 요소로 옮겨 버려, 이후 키 입력이 캔버스로 오지 않는다.
      ev.preventDefault()
      setMode(mode === 'game' ? 'build' : 'game')
      return
    }

    // 아래는 게임 모드 전용이다. 배치 모드에서 스페이스로 페이즈가 넘어가면
    // 소품을 고르던 중에 라운드가 끝나 버린다.
    if (mode !== 'game' || ev.repeat) return

    if (ev.code === SKIP_PHASE_KEY) {
      // 스페이스는 기본적으로 화면을 스크롤하고, 포커스된 버튼을 누른다. 둘 다 여기서는 사고다.
      ev.preventDefault()
      stage.session.skipPhase()
      return
    }

    if (ev.code === ORIGIN_WARP_KEY) {
      ev.preventDefault()
      stage.session.toggleOrigin()
    }
  }

  window.addEventListener('keydown', handleKeyDown)

  return {
    getMode: () => mode,
    setMode,
    resetToGameMode: () => {
      mode = 'game'
    },
    tapAt,
    refresh,

    dispose(): void {
      window.removeEventListener('keydown', handleKeyDown)
      gestures.dispose()
      controls.dispose()
    },
  }
}
